import { useMemo, useState } from "react";
import { DownloadSimple, Copy, Check, ShareNetwork, InstagramLogo, LinkedinLogo } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const FORMATS = [
  { key: "ig", label: "Instagram post", size: "1080 × 1350", icon: InstagramLogo, ratio: "aspect-[4/5]" },
  { key: "story", label: "Instagram story", size: "1080 × 1920", icon: InstagramLogo, ratio: "aspect-[9/16]" },
  { key: "li", label: "LinkedIn", size: "1200 × 627", icon: LinkedinLogo, ratio: "aspect-[1200/627]" },
];

// Ready-to-post share card for a press feature. The graphic is rendered
// server-side from the same stored photo, so it is identical everywhere.
export default function PressShareBlock({ item, compact = false }) {
  const [fmt, setFmt] = useState("ig");
  const [open, setOpen] = useState(!compact);
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);

  const active = FORMATS.find((f) => f.key === fmt) || FORMATS[0];
  const cardUrl = useMemo(
    () => `${BACKEND}/api/share/press-card.png?format=${fmt}&id=${item?.shareCardId || ""}`,
    [fmt, item],
  );
  const fileName = `sweat-for-sacrifice-${(item?.outlet || "press").toLowerCase()}-${fmt}.png`;

  if (!item) return null;

  const download = async () => {
    setBusy(true);
    try {
      const res = await fetch(cardUrl);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Share card downloaded — post it and tag @sweatwithslay");
    } catch {
      window.open(cardUrl, "_blank", "noopener");
    }
    setBusy(false);
  };

  const nativeShare = async () => {
    try {
      const res = await fetch(cardUrl);
      const blob = await res.blob();
      const file = new File([blob], fileName, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text: item.shareCaption });
        return;
      }
      download();
    } catch {
      download();
    }
  };

  const copyCaption = async () => {
    try {
      await navigator.clipboard.writeText(item.shareCaption);
      setCopied(true);
      toast.success("Caption copied — paste it with the image");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Copy failed — select the text below and copy manually");
    }
  };

  if (compact && !open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="press-share-open"
        className="inline-flex items-center gap-2 border border-white/25 hover:border-[#B32027] hover:bg-[#B32027]/10 text-white px-6 py-4 text-[10px] font-bold uppercase tracking-[0.25em] transition-colors"
      >
        <ShareNetwork size={13} weight="bold" /> Share this feature
      </button>
    );
  }

  return (
    <div
      data-testid="press-share-block"
      className="border border-white/10 bg-[#0F0F0F] p-5 md:p-8"
    >
      <p className="text-[#B32027] uppercase tracking-[0.35em] text-[11px] font-bold mb-3">
        / Share this feature
      </p>
      <h3 className="font-display font-black uppercase tracking-tight leading-[0.95] text-xl md:text-2xl mb-3">
        Post it to Instagram or LinkedIn
      </h3>
      <p className="text-white/60 text-sm leading-relaxed mb-6 max-w-xl">
        Pick a size, download the card, then paste the caption. Every version
        uses the same photo and credit — nothing gets cropped.
      </p>

      <div className="flex flex-wrap gap-2 mb-6">
        {FORMATS.map((f) => {
          const Icon = f.icon;
          const on = f.key === fmt;
          return (
            <button
              key={f.key}
              type="button"
              onClick={() => setFmt(f.key)}
              data-testid={`press-share-format-${f.key}`}
              className={`inline-flex items-center gap-2 px-4 py-3 text-[10px] font-bold uppercase tracking-[0.2em] border transition-colors ${
                on
                  ? "bg-[#B32027] border-[#B32027] text-white"
                  : "bg-transparent border-white/20 text-white/70 hover:border-white/50 hover:text-white"
              }`}
            >
              <Icon size={13} weight="fill" /> {f.label}
              <span className="hidden sm:inline text-white/50 font-medium tracking-normal normal-case">
                {f.size}
              </span>
            </button>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,320px)_1fr] gap-7">
        <div>
          <div className={`${active.ratio} bg-black border border-white/10 overflow-hidden`}>
            <img
              key={fmt}
              src={cardUrl}
              alt={`${item.outlet} feature share card — ${active.label}`}
              data-testid="press-share-preview"
              className="w-full h-full object-contain"
            />
          </div>
          <div className="flex flex-col sm:flex-row gap-3 mt-4">
            <button
              type="button"
              onClick={download}
              disabled={busy}
              data-testid="press-share-download"
              className="flex-1 inline-flex items-center justify-center gap-2 bg-[#B32027] hover:bg-[#D42730] disabled:opacity-60 text-white px-5 py-4 text-[10px] font-bold uppercase tracking-[0.25em] transition-colors"
            >
              <DownloadSimple size={13} weight="bold" />
              {busy ? "Building…" : "Download image"}
            </button>
            <button
              type="button"
              onClick={nativeShare}
              data-testid="press-share-native"
              className="inline-flex items-center justify-center gap-2 border border-white/25 hover:border-white text-white px-5 py-4 text-[10px] font-bold uppercase tracking-[0.25em] transition-colors"
            >
              <ShareNetwork size={13} weight="bold" /> Share
            </button>
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-3">
            <p className="text-[10px] uppercase tracking-[0.25em] text-white/50 font-bold">
              Caption
            </p>
            <button
              type="button"
              onClick={copyCaption}
              data-testid="press-share-copy-caption"
              className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-white/80 hover:text-[#B32027] transition-colors"
            >
              {copied ? <Check size={13} weight="bold" /> : <Copy size={13} weight="bold" />}
              {copied ? "Copied" : "Copy caption"}
            </button>
          </div>
          <textarea
            readOnly
            value={item.shareCaption}
            data-testid="press-share-caption"
            rows={14}
            onFocus={(e) => e.target.select()}
            className="w-full bg-black/60 border border-white/10 text-white/80 text-sm leading-relaxed p-4 font-body resize-none focus:outline-none focus:border-[#B32027]"
          />
          <p className="text-white/40 text-xs mt-3">
            Instagram doesn&rsquo;t allow clickable links in captions — put
            sweatwithslay.com/legacy in your bio or add a link sticker to the story.
          </p>
        </div>
      </div>
    </div>
  );
}
